import { FormProvider, useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Box,
  Grid
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import React, { Dispatch, FC } from 'react';
import {
  THistoryDiffData,
  THistorySearchForm
} from '../../../../types/history';
import { HistoryDiff } from '../HistoryDiff';
import { useHooks } from './useHooks';
import { useStyles } from './useStyles';
import { DEFAULT_MESSAGE_NO_DATA } from '../../../../const/Const';
import { MultipleSelectfield } from '../../input/MultipleSelectfield';
import { EditButton } from '../../../atoms/Button';
import { TSelectItem } from '../../../../types/common';
import LoadingIcon from '../../../atoms/LoadingIcon';
import { SiteProcessSelectBox } from '../../input/ProcessSelectBox';
import Loading from '../../../molecules/Loading';

export type TProps = {
  id: number; // 取得する取得履歴のid
  apiHistory: (
    id: number,
    choiceItem?: number[],
    userId?: number,
    page?: number,
    displayLimit?: number
  ) => Promise<any>; // 履歴取得用のapi。idを渡して検索を行うapi。
  createHistoryText: (apiResponseHistory: any) => string; // apiから取得した履歴データから表示する更新内容を作成する。
  apiHistoryDiff: (historyId: number) => Promise<any>; // 履歴詳細取得用のapi。履歴取得用apiから取得したデータのidを渡してデータを取得する。apiからのレスポンスを定義できないのでany型,
  createHistoryDiffList: (
    apiResponseHistoryDiff: any
  ) => Array<THistoryDiffData>; // 履歴取得詳細専用のapiのレスポンスから画面に表示する差分データを作成する
  isOpen: boolean; // モーダルの表示/非表示
  setIsOpen: Dispatch<boolean>;
  title: string; // モーダルのタイトル
  itemList: Array<TSelectItem>; //選択項目リスト
};

/**
 * 変更履歴を表示するモーダル
 * 各データの変更履歴取得apiと変更履歴詳細取得apiのリクエストパラメータとレスポンスが次の場合に利用できる
 * :リクエストパラメータ
 *  id: number を渡す
 * :変更履歴のレスポンス
 * {
 *   data: Array<{ id: number, added_division: number, added_at: string, added_user_name: string }>
 *   page_count": number
 *   onClickCloseButton
 * }
 * :変更履歴詳細のレスポンス
 * {
 *   data: Object
 * }
 * @param props 以下の内容をとる
 *   id: apiに渡すリクエストパラメータ。取得する変更履歴のid
 *   apiHistory: 変更履歴一覧を取得するapiを渡す
 *   createHistoryText: 取得した変更履歴を引数に受けとり、その変更履歴を使って画面に表示するテキストを返す関数を実装し渡す
 *   apiHistoryDiff: 変更履歴詳細を取得するapiを渡す
 *   createHistoryDiffList: 取得した変更履歴詳細を引数に受け取り、画面に表示するデータ（データのid、差分があるか、レスポンスで受け取った変更後データと変更前データ）を返す関数を実装し渡す
 *   isOpen: モーダルの表示/非表示の制御
 *   setIsOpen: isOpenの設定を行う
 *   title: モーダルに表示するタイトル
 * @returns
 */
export const HistoryModal: FC<TProps> = props => {
  const {
    id,
    apiHistory,
    createHistoryText,
    apiHistoryDiff,
    createHistoryDiffList,
    isOpen,
    setIsOpen,
    title,
    itemList
  } = props;

  const methods = useForm<THistorySearchForm>({
    defaultValues: {
      user_id: '0'
    }
  });
  const { reset, handleSubmit } = methods;

  const {
    historyDataList,
    lastElementRef,
    onClickCloseButton,
    userList,
    handleSearchClick,
    isLoading,
    isLoadingPage
  } = useHooks({
    id,
    itemList,
    apiHistory,
    createHistoryText,
    isOpen,
    setIsOpen,
    reset
  });

  const classes = useStyles();

  return (
    <>
      <Dialog
        aria-labelledby='modal-title'
        open={isOpen}
        onClose={() => {
          onClickCloseButton();
        }}
        closeAfterTransition
        className={classes.modal}
        maxWidth={'sm'}
        PaperProps={{ style: { margin: 5 } }}
      >
        <Loading open={isLoadingPage} />
        <DialogTitle disableTypography className={classes.title}>
          <Typography variant='h6'>{title}</Typography>
          <IconButton
            aria-label='close'
            className={classes.closeButton}
            onClick={onClickCloseButton}
          >
            <Close />
          </IconButton>

          {/* 検索フォーム */}
          <FormProvider {...methods}>
            <Grid container spacing={1} alignItems='center'>
              <Grid item xs={6} sm={5}>
                {/* 項目名セレクトボックス */}
                <MultipleSelectfield
                  name='choice_item'
                  title='項目名'
                  dataList={itemList}
                />
              </Grid>
              <Grid item xs={6} sm={5}>
                {/* 更新者セレクトボックス */}
                <Box className={classes.selectBoxContainer}>
                  <SiteProcessSelectBox
                    name='user_id'
                    dataList={userList}
                    className={classes.selectBox}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Grid container justify='flex-end'>
                  <Grid item>
                    <EditButton onClick={handleSubmit(handleSearchClick)}>
                      検索
                    </EditButton>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </FormProvider>
        </DialogTitle>
        {isLoading ? (
          <Grid
            container
            alignItems='center'
            justify='center'
            className={classes.loadingContainer}
          >
            <LoadingIcon />
          </Grid>
        ) : (
          <DialogContent
            dividers={true}
            className={classes.contentContainer}
            id='hisrotyModalContent'
          >
            {!historyDataList || historyDataList.length === 0 ? (
              <div className={classes.noDataMessage}>
                {DEFAULT_MESSAGE_NO_DATA}
              </div>
            ) : (
              historyDataList.map((historyData, index) => {
                return (
                  <React.Fragment key={historyData.id}>
                    <HistoryDiff
                      historyData={historyData}
                      createHistoryDiffList={createHistoryDiffList}
                      apiRequest={apiHistoryDiff}
                      callbackRef={
                        index + 1 === historyDataList.length
                          ? lastElementRef
                          : undefined
                      }
                    />
                  </React.Fragment>
                );
              })
            )}
          </DialogContent>
        )}
      </Dialog>
    </>
  );
};
